/** Mortar (TOWERS_V2): lobbed shells that lead their target along the road, land after a flight time and burst. */
import { DamageType, TowerArchetype } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared, positionAt } from "../../world/roadGeometry";
import { Enemy, Shell, Tower } from "../entities";
import { SimEventKind } from "../events";
import { LevelSim } from "../levelSim";
import { mythicValue } from "../mythics";
import { applyDamage, applyHit } from "./combat";
import { BASE_TIERS } from "../branches/ascension";
import { splashOf } from "../branches/shots";
import { onShellLanded } from "../branches/shells";

/**
 * Lobs a shell at where `target` will be when it lands: its road distance plus its step (slow included, none while a
 * troop or soldier holds it) times the flight time, capped at the road's end. The damage is fixed at launch.
 */
export function launchShell(sim: LevelSim, t: Tower, a: TowerArchetype, target: Enemy, damage: Fix64, seek = false): void {
  let step = target.blocked ? Fix64.Zero : target.speedPerTick;
  if (!step.isZero() && target.slowTicksLeft > 0) step = step.mul(Fix64.One.sub(target.slowFraction));
  let at = target.distance.add(step.mulInt(a.shellFlightTicks));
  if (at.gt(sim.road.length)) at = sim.road.length;
  const [x, y] = positionAt(sim.road.roadCells, at);
  const splash = t.tier > BASE_TIERS ? splashOf(sim, t, a) : a.splashRadius;   // tier 4: the Battery's bigger blast, the Rocket's smaller
  const radius = splash.add(mythicValue(sim, t.archetype, sim.mythics.mortarCluster));
  const shell: Shell = { id: sim.nextShellId++, towerId: t.id, archetype: t.archetype, x, y, radius, damage, flightTicks: a.shellFlightTicks, ticksLeft: a.shellFlightTicks, seekId: seek ? target.id : 0 };
  sim.shells.push(shell);
  sim.events.add(SimEventKind.ShellLaunched, shell.id, t.id, a.shellFlightTicks);
}

/** Once per wave tick, before the towers fire: every shell in flight comes one tick closer; those due land, in launch order. */
export function tickShells(sim: LevelSim): void {
  sim.landedShells.length = 0;
  if (sim.shells.length === 0) return;
  let kept = 0;
  for (let i = 0; i < sim.shells.length; i++) {
    const s = sim.shells[i];
    s.ticksLeft--;
    if (s.ticksLeft > 0) { sim.shells[kept++] = s; continue; }
    land(sim, s);
  }
  sim.shells.length = kept;
}

/** Lands every shell still in flight at once (a stage cleared): nothing is left on the field to hit, so none hits. */
export function landAllShells(sim: LevelSim): void {
  for (const s of sim.shells) { s.ticksLeft = 0; land(sim, s); }
  sim.shells.length = 0;
}

function land(sim: LevelSim, s: Shell): void {
  const a = sim.balance.towers[s.archetype];
  const tower = sim.findTower(s.towerId);
  // Seeker Shells (tier 4, Rocket Mortar): the shell lands where its target is now, and its burst reaches flyers too.
  if (s.seekId !== 0) {
    const target = sim.findEnemy(s.seekId);
    if (target !== null) { s.x = target.x; s.y = target.y; }
  }
  const r2 = s.radius.mul(s.radius);
  const air = a.targetsAir || s.seekId !== 0;
  let hits = 0;
  sim.areaDepth++;                                            // a shell's burst is area damage, its target included
  for (const e of sim.enemySlots) {
    if (!e.alive || (e.isAir ? !air : !a.targetsGround) || !sim.targetable(e)) continue;
    if (distanceSquared(s.x, s.y, e.x, e.y).gt(r2)) continue;
    hits++;
    if (tower !== null) applyHit(sim, e, tower, a, s.damage);
    else applyDamage(sim, e, null, s.damage, DamageType.Physical);       // its Mortar was sold mid-flight
  }
  sim.areaDepth--;
  sim.landedShells.push(s);
  sim.events.add(SimEventKind.ShellLanded, s.id, s.towerId, hits);
  onShellLanded(sim, s, tower);                              // tier 4: Earthquake, Napalm
}
